import { authedFetch, errorFromResponse } from './api';

/**
 * Pastor Rob's book, Genius, and The Net's channels: the links the menu, the
 * reader and the lines under an answer share. The reader's text comes from
 * functions/book.js (bookExcerpt), a few paragraphs at a time.
 */

export const BOOK_TITLE = 'Genius: Think Like Christ. Live Like Christ.';
export const BOOK_AUTHOR = 'Rob Hall';
export const BOOK_AMAZON_URL = 'https://a.co/d/0fWgYT3J';

// Plain words, no logos; the click counter reports them by these labels.
export const THE_NET_SOCIALS = [
  { label: 'Facebook', href: 'https://www.facebook.com/thenethsv' },
  { label: 'Instagram', href: 'https://www.instagram.com/thenetcommunitychurch/' },
  { label: 'YouTube', href: 'https://www.youtube.com/channel/UCRM1NAz4KzlwvWIvYEP-Ovw' },
];
export const THE_NET_YOUTUBE_URL = THE_NET_SOCIALS[2].href;

export const QUOTE_MAX = 300;

const WORD_CHAR = /[\p{L}\p{M}\p{N}’‘'`´]/u;

/** At most `max` characters of `text`, never ending inside a word: the reader looks for whole words (functions/book.js). */
export function clipWords(text, max = QUOTE_MAX) {
  const s = String(text || '');
  if (s.length <= max) return s;
  const head = s.slice(0, max);
  return WORD_CHAR.test(head[head.length - 1]) && WORD_CHAR.test(s[max]) ? head.replace(/[\p{L}\p{M}\p{N}’‘'`´]+$/u, '') : head;
}

/** "Tell me more about this part of Genius: The Genius Of Sex". */
export function askAboutPassage(title) {
  return `Tell me more about this part of Genius: ${String(title || 'this chapter').trim()}`;
}

const NUMBER_WORDS = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];

/** The chapter a source's title names ("… — Chapter Eleven: The Genius Of Sex" → 11, "Introduction" → 0), or null. */
export function chapterFromTitle(title) {
  const m = String(title || '').match(/\b(introduction|chapter\s+([a-z]+|\d+))\b/i);
  if (!m) return null;
  if (!m[2]) return 0;
  const n = /^\d+$/.test(m[2]) ? Number(m[2]) : NUMBER_WORDS.indexOf(m[2].toLowerCase());
  return n > 0 && n <= 20 ? n : null;
}

/** The chapter's name for the reader's fallback and the ask: "The Genius Of Sex", else "Chapter Eleven". */
export function chapterNameFromTitle(title) {
  const tail = String(title || '').split(/—|·/).pop().trim();
  const named = tail.match(/^(?:introduction|chapter\s+\w+)\s*:\s*(.+)$/i);
  return named ? named[1].trim() : tail;
}

const BLOCKS = 'p, li, blockquote, h1, h2, h3';
// Not the answer's words: the numbers, their spoken names, and the cited titles.
const NOT_WORDS = 'sup.cv-cite, cite.cv-cite-name';

/** The text of `block`'s words before (or after) `marker`, citations left out. */
function textAround(block, marker, side) {
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  const parts = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.parentElement && node.parentElement.closest(NOT_WORDS)) continue;
    // eslint-disable-next-line no-bitwise
    const before = Boolean(marker.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_PRECEDING);
    if ((side === 'before') === before) parts.push(node.textContent);
  }
  return parts.join('').replace(/\s+/g, ' ').trim();
}

/** The last one or two sentences of `text`, at most `max` characters. */
function lastSentences(text, max) {
  const sentences = text.match(/[^.!?…]+[.!?…]*["”’)]*\s*/g) || [text];
  let out = '';
  for (let i = sentences.length - 1; i >= 0 && i >= sentences.length - 2; i--) {
    const next = sentences[i] + out;
    if (next.trim().length > max && out) break;
    out = next;
  }
  out = out.trim();
  return out.length > max ? out.slice(out.length - max).replace(/^\S*\s/, '') : out;
}

/**
 * What the answer said just before a book citation: the sentence or two
 * before the marker, 300 characters at most. A citation that opens its
 * sentence ("In Genius, Chapter Eleven, Rob writes …") has its words after
 * it, so those are used when there is little before.
 */
export function quoteNear(marker) {
  if (!marker) return '';
  const block = marker.closest(BLOCKS) || marker.parentElement;
  if (!block) return '';
  let quote = lastSentences(textAround(block, marker, 'before'), QUOTE_MAX);
  if (quote.length < 60) {
    const after = textAround(block, marker, 'after');
    quote = clipWords(`${quote} ${after}`.trim());
  }
  return quote;
}

/** An answer's opening words, for a book source that no sentence cites. */
export function quoteFromAnswer(answerEl) {
  if (!answerEl) return '';
  const clone = answerEl.cloneNode(true);
  clone.querySelectorAll(NOT_WORDS).forEach((el) => el.remove());
  return clipWords(clone.textContent.replace(/\s+/g, ' ').trim());
}

/**
 * The passage around a citation: { available, chapter, label, title,
 * paragraphs: [{ text, heading? }], highlight, start, marks, anchor }
 * (functions/book.js). `quote`: the words the server verified in the book, or
 * the answer's words before the citation; `para`: where the server said the
 * answer read the chapter (data-book-para), honoured only when it granted
 * that paragraph to this person.
 */
export async function fetchExcerpt({ chapter, quote, para = null }) {
  const res = await authedFetch('bookExcerpt', {
    chapter,
    quote: clipWords(quote),
    ...(Number.isInteger(para) && para >= 0 ? { para } : {}),
  });
  if (!res.ok) throw await errorFromResponse(res);
  return res.json();
}

/**
 * What to open the reader with for one book citation in an answer: the
 * quote the server verified in that chapter (the one in the citation's own
 * paragraph or blockquote when there are two), else null; the caller falls
 * back to the answer's words (quoteNear).
 */
export function verifiedQuoteFor(sources, chapter, blockText = '') {
  const source = (sources || []).find((s) => s && s.contentType === 'book' && s.chapter === chapter && Array.isArray(s.quotes) && s.quotes.length);
  if (!source) return null;
  const words = (t) => String(t || '').toLowerCase().replace(/[’‘]/g, "'").replace(/[^a-z0-9']+/g, ' ').trim();
  const block = words(blockText);
  return source.quotes.find((q) => block && block.includes(words(q))) || source.quotes[0];
}
